import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { potentialBandOf } from '../../task-cassette/task-cassette-model';
import { taskCassetteLabels } from '../../task-cassette/task-cassette-translations';
import type { ProfileTaskEffort } from '../model/profile-task';
import type { BusinessImpact } from './automation-assessment-model';
import { toPercent } from './automation-assessment-scoring';
import {
    automationAssessmentTranslationKeys,
    businessImpactTranslationKeys,
} from './automation-assessment-translations';

const VALUE_CLASS_NAME = 'm-0 text-h3 font-normal leading-tight tabular-nums text-text-primary';

const VALUE_BUTTON_CLASS_NAME = `${VALUE_CLASS_NAME} cursor-pointer border-none bg-transparent p-0 underline-offset-4 hover:underline`;

const IMPACT_TRIGGER_CLASS_NAME =
    'cursor-default focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

const Stat: FunctionComponent<{ readonly label: string; readonly value: ReactNode }> = ({ label, value }) => (
    <div className="flex min-w-0 flex-col-reverse items-center gap-xs px-m text-center">
        <dt className="text-caption text-text-muted">{label}</dt>
        <dd className={VALUE_CLASS_NAME}>{value}</dd>
    </div>
);

/**
 * The impact as the percentage the matrix plots. Where the tile can show how it comes about, the value opens that
 * explanation; elsewhere the model's reason, when it gave one, opens on hover and focus.
 */
const BusinessImpactValue: FunctionComponent<{
    readonly impact: BusinessImpact;
    readonly onOpen: (() => void) | undefined;
}> = ({ impact, onOpen }) => {
    const translate = useTranslate();
    const value =
        impact.value === undefined
            ? '–'
            : translate(automationAssessmentTranslationKeys.percent, { value: toPercent(impact.value) });

    if (onOpen !== undefined && impact.value !== undefined) {
        return (
            <Button
                aria-label={`${value}, ${translate(businessImpactTranslationKeys.open)}`}
                className={VALUE_BUTTON_CLASS_NAME}
                variant="plain"
                onClick={onOpen}
            >
                {value}
            </Button>
        );
    }

    if (impact.rationale === undefined) {
        return value;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                {/* biome-ignore lint/a11y/noNoninteractiveTabindex: the reason opens on focus, so keyboard users need to be able to focus the value. */}
                <span className={IMPACT_TRIGGER_CLASS_NAME} tabIndex={0}>
                    {value}
                </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="bottom" sideOffset={8}>
                    {impact.rationale}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};

/**
 * The task at a glance under its heading: its automation potential as a band, its business impact once
 * rated, its weekly effort and how many workflow steps it has. Where the tile can show the assessment in
 * full, the band opens it.
 */
export const TaskStatsRow: FunctionComponent<{
    readonly businessImpact: BusinessImpact | undefined;
    readonly effort: ProfileTaskEffort | undefined;
    /** The backend has queued or started the assessment and the result is not there yet. */
    readonly isAssessing: boolean;
    readonly onOpenAssessment: (() => void) | undefined;
    readonly onOpenBusinessImpact?: (() => void) | undefined;
    readonly stepCount: number;
    /** The assessed share of the task's effort software could take over, 0 to 1. */
    readonly taskScore: number | undefined;
}> = ({ businessImpact, effort, isAssessing, onOpenAssessment, onOpenBusinessImpact, stepCount, taskScore }) => {
    const translate = useTranslate();
    const labels = taskCassetteLabels(translate);
    const minimum = effort?.minimumHoursPerWeek ?? effort?.maximumHoursPerWeek;
    const maximum = effort?.maximumHoursPerWeek ?? effort?.minimumHoursPerWeek;
    const band = taskScore === undefined ? undefined : labels.bands[potentialBandOf(taskScore)];

    const potential =
        band !== undefined ? (
            onOpenAssessment === undefined ? (
                band
            ) : (
                <Button
                    aria-label={`${band}, ${translate(automationAssessmentTranslationKeys.openDetails)}`}
                    className={VALUE_BUTTON_CLASS_NAME}
                    variant="plain"
                    onClick={onOpenAssessment}
                >
                    {band}
                </Button>
            )
        ) : isAssessing ? (
            <SkeletonBase
                aria-label={translate(automationAssessmentTranslationKeys.pending)}
                className="mx-auto my-xs h-6 w-16 animate-none!"
                role="status"
            />
        ) : (
            <>
                <span aria-hidden={true}>–</span>
                <span className="sr-only">{labels.notAssessed}</span>
            </>
        );

    return (
        <dl
            className={`m-0 grid ${businessImpact === undefined ? 'grid-cols-3' : 'grid-cols-4'} divide-x divide-border-subtle border-y border-border-subtle py-xl`}
            data-testid="task-stats"
        >
            <Stat label={labels.automationPotential} value={potential} />
            {businessImpact === undefined ? null : (
                <Stat
                    label={translate(automationAssessmentTranslationKeys.businessImpact)}
                    value={<BusinessImpactValue impact={businessImpact} onOpen={onOpenBusinessImpact} />}
                />
            )}
            <Stat
                label={labels.perWeek}
                value={minimum === undefined || maximum === undefined ? '–' : labels.effort(minimum, maximum)}
            />
            <Stat label={labels.workflowSteps} value={stepCount} />
        </dl>
    );
};
